import React, { useState } from "react";
import {
    Card,
    Radio,
    Form,
    Button,
    Input,
    Alert,
    Row,
    Col,
    Select,
    Divider,
    message,
} from "antd";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import useSubscribe from "../../hooks/useSubscribe";

const { Option } = Select;

export default function SubscriptionCheckout({ userId }) {
    const [form] = Form.useForm();
    const stripe = useStripe();
    const elements = useElements();

    // UI state
    const [method, setMethod] = useState("card"); // card|paypal|mobile|bank
    const [loading, setLoading] = useState(false);
    const [plan, setPlan] = useState("price_agri_hero_monthly");
    const [email, setEmail] = useState("");
    const { subscribe, status, error } = useSubscribe({ userId, onStatusChange: (s) => {
            // optional callback called on status updates (from websocket/polling)
            if (s === "SUCCEEDED") message.success("Paiement confirmé !");
            if (s === "FAILED") message.error("Paiement échoué.");
        }});

    const handleSubmit = async () => {
        setLoading(true);
        try {
            if (method === "card") {
                if (!stripe || !elements) {
                    message.error("Stripe non initialisé");
                    setLoading(false);
                    return;
                }
                // create PaymentMethod
                const card = elements.getElement(CardElement);
                const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
                    type: "card",
                    card,
                    billing_details: { email },
                });
                if (pmError) throw pmError;
                // call the hook to subscribe using paymentMethod.id
                await subscribe({
                    provider: "STRIPE",
                    paymentMethodId: paymentMethod.id,
                    plan,
                    billingCycle: "monthly",
                    amount: null,
                    payerEmail: email,
                });
            } else if (method === "paypal") {
                // backend will provide a checkoutUrl for PayPal flow
                await subscribe({
                    provider: "PAYPAL",
                    plan,
                    billingCycle: "monthly",
                    payerEmail: email,
                });
            } else if (method === "mobile") {
                await subscribe({
                    provider: "MOBILE",
                    plan,
                    billingCycle: "postHarvest",
                    payerEmail: email,
                    mobileNumber: form.getFieldValue("mobileNumber"),
                    mobileProvider: form.getFieldValue("mobileProvider"),
                });
            } else if (method === "bank") {
                await subscribe({
                    provider: "BANK_TRANSFER",
                    plan,
                    billingCycle: "perSeason",
                    payerEmail: email,
                    accountHolder: form.getFieldValue("accountHolder"),
                    iban: form.getFieldValue("iban"),
                });
            }
        } catch (err) {
            console.error(err);
            message.error(err.message || "Erreur lors de l'initiation du paiement");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ maxWidth: 900, margin: "0 auto", padding: 24 }}>
            <Card title="Souscrire - AGRI HERO">
                <Form layout="vertical" form={form}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item label="Email" required>
                                <Input value={email} onChange={(e) => setEmail(e.target.value)} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Forfait">
                                <Select value={plan} onChange={(v) => setPlan(v)}>
                                    <Option value="price_agri_hero_monthly">AGRI_HERO - Mensuel</Option>
                                    <Option value="price_agri_hero_yearly">AGRI_HERO - Annuel</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider />

                    <Form.Item label="Méthode de paiement">
                        <Radio.Group onChange={(e) => setMethod(e.target.value)} value={method}>
                            <Radio value="card">Carte (Stripe)</Radio>
                            <Radio value="paypal">PayPal</Radio>
                            <Radio value="mobile">Mobile Money</Radio>
                            <Radio value="bank">Virement Bancaire</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {method === "card" && (
                        <>
                            <Form.Item label="Détails de la carte">
                                <CardElement options={{ hidePostalCode: true, style: { base: { fontSize: "16px" } } }} />
                            </Form.Item>
                        </>
                    )}

                    {method === "paypal" && (
                        <Alert type="info" message="Vous serez redirigé vers PayPal pour finaliser le paiement." />
                    )}

                    {method === "mobile" && (
                        <>
                            <Form.Item label="Numéro mobile" name="mobileNumber">
                                <Input placeholder="+22890000000" />
                            </Form.Item>
                            <Form.Item label="Opérateur" name="mobileProvider">
                                <Input placeholder="Tigo, MTN, Orange..." />
                            </Form.Item>
                        </>
                    )}

                    {method === "bank" && (
                        <>
                            <Form.Item label="Titulaire du compte" name="accountHolder">
                                <Input />
                            </Form.Item>
                            <Form.Item label="IBAN / RIB" name="iban">
                                <Input />
                            </Form.Item>
                        </>
                    )}

                    <Divider />

                    <Form.Item>
                        <Button type="primary" loading={loading} onClick={handleSubmit}>
                            Effectuer le paiement
                        </Button>
                    </Form.Item>

                    {status && <Alert message={`Statut du paiement : ${status}`} style={{ marginTop: 12 }} />}
                    {error && <Alert type="error" message={error} style={{ marginTop: 12 }} />}
                </Form>
            </Card>
        </div>
    );
}
